import type { PrinterConnectMode } from '../BluetoothPrinterService';
import { getEffectivePrinterLinkSettings, getStoredPrinterModelId } from '../printerModelConfig';
import { shouldUseXpP203aTspl } from '../xpP203aLabelConfig';
import { buildPayzheBtpSettingsPayload } from './buildSettingsPayload';
import {
  fetchPayzheBtpPrinters,
  fetchPayzheBtpStatus,
  getStoredPayzheBtpToken,
  postPayzheBtpConnect,
  postPayzheBtpDisconnect,
  postPayzheBtpPrint,
  postPayzheBtpSettings,
  waitForPayzheBtpJob,
  type PayzheBtpPrinterListPayload,
} from './nativeBtpClient';
import { sleep } from './mobilePlatform';
import { mapNativeErrorToPayzheBtpError, PayzheBtpError } from './PayzheBtpError';
import { payzheBtpProvider } from './PayzheBtpProvider';

function connectModeToNativeTransport(
  mode: PrinterConnectMode,
): 'auto' | 'ble' | 'classic-serial' {
  if (mode === 'ble') return 'ble';
  if (mode === 'bluetooth-serial') return 'classic-serial';
  return 'auto';
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

function createRequestId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export async function pickDefaultPrinterId(
  printers: PayzheBtpPrinterListPayload,
): Promise<string | null> {
  return (
    printers.current?.id ??
    printers.recent?.[0]?.id ??
    printers.paired[0]?.id ??
    printers.discovered.find((d) => d.paired)?.id ??
    printers.discovered[0]?.id ??
    null
  );
}

async function waitForConnected(token: string, timeoutMs = 25_000): Promise<boolean> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const status = await fetchPayzheBtpStatus(token);
    await payzheBtpProvider.refreshStatus();
    if (status.connected) return true;
    await sleep(800);
  }
  return false;
}

export class NativeBtpPrinterAdapter {
  async connect(mode: PrinterConnectMode, printerId?: string): Promise<void> {
    try {
      await payzheBtpProvider.ensureReady({ wakeApp: false });
      const token = getStoredPayzheBtpToken();
      if (!token) {
        throw new PayzheBtpError(
          'No auth token',
          'auth_failed',
          'bluetoothPrinter.payzheBtp.errors.authFailed',
          're_handshake',
        );
      }

      await postPayzheBtpSettings(buildPayzheBtpSettingsPayload(), token);

      const alreadyConnected = payzheBtpProvider.getSnapshot().status?.connected;
      if (alreadyConnected) {
        await payzheBtpProvider.refreshStatus();
        return;
      }

      let targetId = printerId;
      if (!targetId) {
        const printers = await fetchPayzheBtpPrinters(token);
        targetId = (await pickDefaultPrinterId(printers)) ?? undefined;
      }

      if (!targetId) {
        throw new PayzheBtpError(
          'No printer found',
          'printer_not_found',
          'bluetoothPrinter.payzheBtp.errors.printerNotFound',
          'open_printer_page',
        );
      }

      await postPayzheBtpConnect(
        { printerId: targetId, transport: connectModeToNativeTransport(mode) },
        token,
      );

      const connected = await waitForConnected(token);
      if (!connected) {
        throw new PayzheBtpError(
          'Connection failed',
          'connect_failed',
          'bluetoothPrinter.payzheBtp.errors.connectFailed',
          'retry',
        );
      }

      await payzheBtpProvider.refreshStatus();
    } catch (error) {
      throw mapNativeErrorToPayzheBtpError(error);
    }
  }

  async disconnect(): Promise<void> {
    const token = getStoredPayzheBtpToken();
    if (!token) return;
    try {
      await postPayzheBtpDisconnect(token);
      await payzheBtpProvider.refreshStatus();
    } catch (error) {
      throw mapNativeErrorToPayzheBtpError(error);
    }
  }

  async print(bytes: Uint8Array, options?: { copies?: number; requestId?: string }): Promise<void> {
    try {
      await payzheBtpProvider.ensureReady({ wakeApp: 'auto' });
      const token = getStoredPayzheBtpToken();
      if (!token) {
        throw new PayzheBtpError(
          'No auth token',
          'auth_failed',
          'bluetoothPrinter.payzheBtp.errors.authFailed',
          're_handshake',
        );
      }

      const status = await fetchPayzheBtpStatus(token);
      if (!status.connected) {
        throw new PayzheBtpError(
          'Printer not connected',
          'connect_failed',
          'bluetoothPrinter.errors.notConnected',
          'open_printer_page',
        );
      }

      const link = getEffectivePrinterLinkSettings();
      const useTspl = shouldUseXpP203aTspl(link.modelId);

      const result = await postPayzheBtpPrint(
        {
          requestId: options?.requestId ?? createRequestId('print'),
          payload: { type: 'base64', data: bytesToBase64(bytes) },
          printerModel: getStoredPrinterModelId(),
          encoding: useTspl ? 'tspl' : 'escpos',
          copies: options?.copies ?? 1,
          priority: 'normal',
        },
        token,
      );

      if (result.duplicate && result.status === 'completed') {
        await payzheBtpProvider.refreshStatus();
        return;
      }

      const jobId = result.jobId;
      if (jobId) {
        const job = await waitForPayzheBtpJob(jobId, { token });
        if (job.status !== 'completed') {
          throw new PayzheBtpError(
            job.lastError ?? 'Print job failed',
            'queue_failed',
            'bluetoothPrinter.payzheBtp.printFailedQueue',
            'open_printer_page',
          );
        }
      }

      await payzheBtpProvider.refreshStatus();
    } catch (error) {
      throw mapNativeErrorToPayzheBtpError(error);
    }
  }

  isConnected(): boolean {
    const status = payzheBtpProvider.getSnapshot().status;
    return Boolean(status?.connected);
  }

  getConnectionState(): 'disconnected' | 'connecting' | 'connected' | 'printing' | 'error' {
    const snap = payzheBtpProvider.getSnapshot();
    if (snap.state === 'checking') return 'connecting';
    if (!snap.status) return snap.state === 'unreachable' ? 'error' : 'disconnected';
    const cs = snap.status.connectionState;
    if (cs === 'printing') return 'printing';
    if (cs === 'connecting') return 'connecting';
    if (snap.status.connected) return 'connected';
    if (cs === 'error' || cs === 'failed') return 'error';
    return 'disconnected';
  }
}

export const nativeBtpPrinterAdapter = new NativeBtpPrinterAdapter();
